import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Save, Loader2, Image as ImageIcon, Plus, X, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuthStore, useAppearanceStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import LocalizedField from '../components/admin/LocalizedField';
import TranslateAllButton from '../components/admin/TranslateAllButton';
import AdminHeader from '../components/admin/AdminHeader';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_LOCALIZED = { en: '', nl: '', ro: '', ru: '' };

const coerceLocalized = (v) => {
  if (!v) return { ...EMPTY_LOCALIZED };
  if (typeof v === 'string') return { ...EMPTY_LOCALIZED, en: v };
  return { ...EMPTY_LOCALIZED, ...v };
};

const normalizePayload = (d) => {
  if (!d) return {};
  const clone = JSON.parse(JSON.stringify(d));
  
  const sections = ['hero', 'philosophy', 'journey', 'factory', 'mission', 'cta'];
  sections.forEach(sec => {
    if (!clone[sec]) {
      clone[sec] = {};
    }
  });

  const mapFields = {
    hero: ['eyebrow', 'headline', 'subheadline'],
    philosophy: ['heading', 'body'],
    journey: ['heading', 'body'],
    factory: ['heading', 'body'],
    mission: ['heading', 'body', 'subbody'],
    cta: ['heading', 'body']
  };

  Object.entries(mapFields).forEach(([sec, fields]) => {
    fields.forEach(f => {
      clone[sec][f] = coerceLocalized(clone[sec][f]);
    });
  });
  
  if (clone.factory && Array.isArray(clone.factory.metrics)) {
    clone.factory.metrics = clone.factory.metrics.map((m) => ({
      value: String(m.value || ''),
      unit: String(m.unit || ''),
      label: coerceLocalized(m.label),
    }));
  } else {
    if (clone.factory) clone.factory.metrics = [];
  }
  
  return clone;
};

const ImagePreview = ({ url, label, testid }) => (
  <div className="max-w-md text-left" data-testid={`${testid}-preview`}>
    {url ? (
      <img src={url} alt={label} className="w-full aspect-video object-cover border border-neutral-200" />
    ) : (
      <div
        className="w-full aspect-video border border-dashed border-neutral-200 bg-neutral-50 flex flex-col items-center justify-center text-neutral-400 select-none"
        data-testid={`${testid}-empty`}
      >
        <ImageIcon className="w-6 h-6 mb-2" strokeWidth={1} />
        <span className="text-[11px] uppercase tracking-wider font-mono">No asset context</span>
      </div>
    )}
    {/* ФИКС: Удален невалидный класс mt-2.xl и дубликат mt-2 */}
    <p className="text-[10px] font-mono text-neutral-400 leading-normal mt-2">
      Read-only — asset configurations are managed inside the{' '}
      <Link to="/admin/appearance" className="underline hover:text-black transition-colors">Appearance engine</Link>.
    </p>
  </div>
);

const SectionCard = ({ children, title, eyebrow }) => (
  <section className="border border-neutral-200 p-8 bg-white text-left shadow-[0_2px_8px_rgba(0,0,0,0.01)] rounded-none" data-testid={`about-section-${eyebrow.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-400 font-mono mb-2">{eyebrow}</p>
    <h2 className="text-2xl font-extralight text-black tracking-tight mb-6">{title}</h2>
    <div className="space-y-5">{children}</div>
  </section>
);

const AdminAboutUsPage = () => {
  const navigate = useNavigate();
  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  
  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const fetchAppearance = useAppearanceStore((s) => s.fetchAppearance);
  
  const [hydrated, setHydrated] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/about' } });
    }
  }, [hydrated, navigate]);

  useEffect(() => {
    if (!hydrated) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/about-us`);
        const d = await res.json();
        const normalized = normalizePayload(d);
        if (alive) {
          setData(normalized);
          setLoading(false);
        }
      } catch (e) {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [hydrated]);

  const patchSection = (section) => (key) => (value) => {
    setData((p) => ({ ...p, [section]: { ...(p[section] || {}), [key]: value } }));
  };

  useEffect(() => {
    if (fetchAppearance) fetchAppearance();
  }, [fetchAppearance]);

  const handleSave = async () => {
    if (!data || !tokenFromHook) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/about-us`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenFromHook}` },
        body: JSON.stringify({
          hero: data.hero, philosophy: data.philosophy, journey: data.journey,
          factory: data.factory, mission: data.mission, cta: data.cta,
        }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      const normalizedUpdated = normalizePayload(updated);
      setData(normalizedUpdated);
      toast.success('About Us saved successfully');
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMetric = () => {
    setData((p) => {
      if (!p) return p;
      const currentFactory = p.factory || {};
      const currentMetrics = Array.isArray(currentFactory.metrics) ? currentFactory.metrics : [];
      const freshLabel = { en: '', nl: '', ro: '', ru: '' };
      return {
        ...p,
        factory: {
          ...currentFactory,
          metrics: [...currentMetrics, { value: '', unit: '', label: freshLabel }]
        }
      };
    });
  };

  const handleUpdateMetric = (idx, field, value) => {
    setData((p) => {
      if (!p || !p.factory || !p.factory.metrics) return p;
      const metrics = [...p.factory.metrics];
      if (metrics[idx]) {
        metrics[idx] = { ...metrics[idx], [field]: value };
      }
      return { ...p, factory: { ...p.factory, metrics } };
    });
  };

  const handleRemoveMetric = (idx) => {
    setData((p) => {
      if (!p || !p.factory || !p.factory.metrics) return p;
      return {
        ...p,
        factory: { ...p.factory, metrics: p.factory.metrics.filter((_, i) => i !== idx) },
      };
    });
  };

  const heroImageUrl = useMemo(() => {
    return siteImages['about_hero']?.url || siteImages['about_hero'] || (data?.hero?.image_url) || '';
  }, [siteImages, data]);

  const factoryImageUrl = useMemo(() => {
    return siteImages['about_factory']?.url || siteImages['about_preview']?.url || siteImages['home_about_preview']?.url || (data?.factory?.image_url) || '';
  }, [siteImages, data]);

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center text-xs font-mono text-neutral-400 bg-white">Loading framework cache…</div>;
  if (!userFromHook || userFromHook.role !== 'admin') return null;
  if (loading || !data) return <div className="min-h-screen flex items-center justify-center text-xs font-mono text-neutral-400 bg-white">Querying brand records…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-left font-light" data-testid="admin-about-page">
      <SEO title="About Us · Admin" description="Manage About Us page" path="admin/about" />
      <AdminHeader active="about" />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 sticky top-16 bg-[#FAFAFA]/95 backdrop-blur-md py-5 z-30 border-b border-neutral-200">
          <div>
            <p className="text-[10px] font-medium tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">CMS · Corporate Dossier</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">About Atelier Layout</h1>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            {/* ФИКС: Отрегулирована работа замыкания полей перевода для защиты от залипания стейта */}
            <TranslateAllButton
              testid="about-translate-all"
              className="h-10 bg-white text-neutral-900 border border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center justify-center transition-colors font-medium uppercase tracking-wider"
              style={{ height: '40px', minHeight: '40px' }}
              fields={() => {
                const list = [];
                const add = (section, key) => {
                  if (data && data[section] && data[section][key] !== undefined) {
                    list.push({ value: data[section][key], onChange: patchSection(section)(key) });
                  }
                };
                add('hero', 'eyebrow'); add('hero', 'headline'); add('hero', 'subheadline');
                add('philosophy', 'heading'); add('philosophy', 'body');
                add('journey', 'heading'); add('journey', 'body');
                add('factory', 'heading'); add('factory', 'body');
                add('mission', 'heading'); add('mission', 'body'); add('mission', 'subbody');
                add('cta', 'heading'); add('cta', 'body');
                if (data && data.factory && Array.isArray(data.factory.metrics)) {
                  data.factory.metrics.forEach((m, i) => {
                    list.push({ value: m.label, onChange: (v) => handleUpdateMetric(i, 'label', v) });
                  });
                }
                return list;
              }}
            />
            <a 
              href="/en/about" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="h-10 bg-white text-neutral-900 border border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium gap-1.5 uppercase tracking-wider"
              style={{ height: '40px' }}
            >
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /> Preview Environment
            </a>
            <Button 
              variant="outline"
              size="sm"
              onClick={handleSave} 
              disabled={saving} 
              className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium min-w-[160px] uppercase tracking-wider"
              style={{ height: '40px' }}
              data-testid="about-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" strokeWidth={1.5} /> : <Save className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />}
              {saving ? 'Publishing…' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="space-y-8">
          {/* HERO */}
          <SectionCard eyebrow="Hero" title="Opening Framework block">
            <LocalizedField label="Eyebrow (small contextual tag)" value={data.hero.eyebrow} onChange={patchSection('hero')('eyebrow')} testid="about-hero-eyebrow" />
            <LocalizedField label="Headline (large display title)" value={data.hero.headline} onChange={patchSection('hero')('headline')} testid="about-hero-headline" />
            <LocalizedField label="Subheadline introductory manifesto" multiline rows={2} value={data.hero.subheadline} onChange={patchSection('hero')('subheadline')} testid="about-hero-subheadline" />
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-2">Hero Image secure link</label>
              <ImagePreview url={heroImageUrl} label="Hero asset" testid="about-hero-image" />
            </div>
          </SectionCard>

          {/* PHILOSOPHY */}
          <SectionCard eyebrow="Philosophy" title="Atelier corporate philosophy">
            <LocalizedField label="Heading" value={data.philosophy.heading} onChange={patchSection('philosophy')('heading')} testid="about-philosophy-heading" />
            <LocalizedField label="Body copy context" multiline rows={5} value={data.philosophy.body} onChange={patchSection('philosophy')('body')} testid="about-philosophy-body" />
          </SectionCard>

          {/* JOURNEY */}
          <SectionCard eyebrow="Journey" title="Historical timeline overview">
            <LocalizedField label="Heading" value={data.journey.heading} onChange={patchSection('journey')('heading')} testid="about-journey-heading" />
            <LocalizedField label="Body copy context" multiline rows={5} value={data.journey.body} onChange={patchSection('journey')('body')} testid="about-journey-body" />
          </SectionCard>

          {/* FACTORY */}
          <SectionCard eyebrow="Factory" title="Production infrastructure & metrics">
            <LocalizedField label="Heading" value={data.factory.heading} onChange={patchSection('factory')('heading')} testid="about-factory-heading" />
            <LocalizedField label="Body copy context" multiline rows={5} value={data.factory.body} onChange={patchSection('factory')('body')} testid="about-factory-body" />
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-2">Factory production image</label>
              <ImagePreview url={factoryImageUrl} label="Factory infrastructure" testid="about-factory-image" />
            </div>

            <div className="pt-6 border-t border-neutral-100">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono">Dynamic Production Metrics — value/unit data blocks</label>
                <Button type="button" variant="outline" size="sm" className="rounded-none text-xs" onClick={handleAddMetric} data-testid="about-add-metric">
                  <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Add Node
                </Button>
              </div>
              <div className="space-y-4" data-testid="about-metrics-list">
                {(data.factory.metrics || []).map((m, i) => (
                  <div key={i} className="grid grid-cols-12 gap-3 items-start border border-neutral-200 bg-neutral-50/30 p-4 rounded-none" data-testid={`about-metric-${i}`}>
                    <div className="col-span-3">
                      <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-2">Numeric Value</label>
                      <input type="text" value={m.value} onChange={(e) => handleUpdateMetric(i, 'value', e.target.value)}
                        placeholder="7,000"
                        className="w-full h-11 px-3 border border-neutral-200 bg-white text-neutral-900 text-sm focus:outline-none focus:border-black font-light rounded-none" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-2">Dimension Unit</label>
                      <input type="text" value={m.unit} onChange={(e) => handleUpdateMetric(i, 'unit', e.target.value)}
                        placeholder="sqm"
                        className="w-full h-11 px-3 border border-neutral-200 bg-white text-neutral-900 text-sm focus:outline-none focus:border-black font-light rounded-none" />
                    </div>
                    <div className="col-span-6">
                      <LocalizedField label="Multilingual Label" value={m.label} onChange={(v) => handleUpdateMetric(i, 'label', v)} testid={`about-metric-${i}-label`} />
                    </div>
                    <div className="col-span-1 pt-7">
                      <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveMetric(i)} className="text-red-600 h-11 border-neutral-200 transition-all hover:bg-rose-50 w-full rounded-none">
                        <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>

          {/* MISSION */}
          <SectionCard eyebrow="Mission" title="Global mission statement framework">
            <LocalizedField label="Heading" value={data.mission.heading} onChange={patchSection('mission')('heading')} testid="about-mission-heading" />
            <LocalizedField label="Body copy context" multiline rows={4} value={data.mission.body} onChange={patchSection('mission')('body')} testid="about-mission-body" />
            {/* ФИКС: data-testid заменен на системный контрактный проп testid */}
            <LocalizedField 
              label="Subbody (Small font metadata below core text block)" 
              multiline 
              rows={4} 
              value={data.mission.subbody || EMPTY_LOCALIZED} 
              onChange={patchSection('mission')('subbody')} 
              testid="about-mission-subbody" 
            />
          </SectionCard>

          {/* CTA */}
          <SectionCard eyebrow="CTA" title="Closing project request prompt (Optional)">
            <LocalizedField label="Heading" value={data.cta.heading} onChange={patchSection('cta')('heading')} testid="about-cta-heading" />
            <LocalizedField label="Body text call-to-action" multiline rows={3} value={data.cta.body} onChange={patchSection('cta')('body')} testid="about-cta-body" />
          </SectionCard>
        </div>

        <div className="mt-10 flex justify-end">
          <Button 
            variant="outline"
            size="sm"
            onClick={handleSave} 
            disabled={saving} 
            className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium min-w-[160px] uppercase tracking-wider"
            style={{ height: '40px' }}
            data-testid="about-save-bottom"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" strokeWidth={1.5} /> : <Save className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />}
            Publish Layout Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAboutUsPage;
